import { NoMetaMaskError } from '@web3-react/metamask'

import Image from 'next/image'
import { useCallback, useEffect, useRef, useState } from 'react'
import { SkeletonTheme } from 'react-loading-skeleton'

import * as Styles from './style'
import Grid from 'shared/components/grid'
import Card from 'shared/components/cards'
import SearchIcon from 'public/assets/gallery/searchIcon.svg'
import { InputWithIcon } from 'shared/components/input'
import FilterDropdownV2 from 'shared/components/filterDropdown'
import { FlexCol, FlexRow, SectionContainer } from 'styles/styled'
import CustomModal from 'shared/components/modals/CustomModal'
import Modal from 'shared/components/modals/Modal'

import StatusModal from 'shared/components/modals/StatusModal'
import { useAppDispatch, useAppSelector } from 'src/logic/redux/hooks'
import {
  ArributesType,
  NftStatusEnum,
  TransactionEnum,
  getNFTs,
  mintNFTS,
  mintNFTSFiat,
  setNftStatus,
  setPage,
  setSelectedNFTs,
  setTransactionHash,
  setTransactionMessage,
  setTransactionStatus,
} from 'src/logic/redux/slices/nftsSlice'
import SelectedNFTsCard from './SelectedNFTsCard'
import {
  setShowConnectModal,
  setShowRegisterModal,
  setShowSuccessModal,
  setShowWrongNetworkModal,
  setSuccessMessage,
  showToast,
} from 'src/logic/redux/slices/uiSlice'
import CheckIcon from 'src/assets/svgs/CheckIcon'
import { WalletTypeEnum } from 'src/logic/redux/slices/userSlice'
import { ToastType } from 'shared/constants'
import { WALLET_TYPES, getConnector } from 'src/blockchain'
import SplitButton from 'shared/components/buttons/SplitButton'
import { colors } from 'styles/theme'
import { SettingsSvg } from 'shared/images'
import LoadingCard from 'shared/components/cards/LoadingCard'
import useWindowDimensions from 'shared/hooks/useWindowDimensions'
import useDebounce from 'shared/hooks/useDebounce'
import { InputContainer } from 'shared/components/input/style'
import { Switch } from 'shared/components/switch'
import { useConnectWallet } from 'src/blockchain/hooks/useConnectWallet'
import { getConfig } from 'config'

const filterOptionList = [
  {
    traitType: 'VTR Membership',
    value: ['Lifetime'],
  },
  {
    traitType: 'Rarity',
    value: ['Legendary'],
  },
]

const Gallery = () => {
  const dispatch = useAppDispatch()
  const [isTermsConditionsApproved, setIsTermsConditionsApproved] =
    useState(false)

  const { account, active, error, activate, provider, chainId } =
    useConnectWallet()

  const [attributes, setAttributes] = useState<ArributesType[]>([])
  const [showMobileFilter, setShowMobileFilter] = useState(false)
  const [showAvailableNfts, setShowAvailableNfts] = useState(true)

  const { user } = useAppSelector((state) => state.userReducer)
  const {
    cryptoMethod,
    selectedNFTs,
    transactionStatus,
    nfts,
    paymentMethod,
    loading,
    fiatLoading,
    page,
    nftStatus,
  } = useAppSelector((state) => state.nftReducer)

  const [search, setSearch] = useState('')
  const debouncedSearchTerm = useDebounce(search, 500)

  const { isMobile } = useWindowDimensions()

  useEffect(() => {
    dispatch(
      getNFTs({
        limit: 12,
        page,
        search,
        attributes,
        nftStatus,
      })
    )
  }, [page, attributes, debouncedSearchTerm, nftStatus])

  useEffect(() => {
    dispatch(setPage(1))
  }, [debouncedSearchTerm])

  const [showConfirmMintModal, setShowConfirmMintModal] = useState(false)

  const handleNftClick = (selectedNft: any) => {
    if (selectedNFTs?.find((f) => f._id === selectedNft._id)) {
      dispatch(
        setSelectedNFTs(selectedNFTs?.filter((f) => f._id !== selectedNft._id))
      )
    } else if (selectedNFTs.length < 20) {
      dispatch(setSelectedNFTs([...selectedNFTs, { ...selectedNft }]))
    } else {
      dispatch(
        showToast({
          message: 'Max 20 NFTs can be minted at a time',
          toastType: ToastType.warning,
        })
      )
    }
  }

  const handleAttributesSelect = (trait: string, value: string) => {
    const attributesArray = attributes?.find((f) => f.value.includes(value))
    if (attributesArray) {
      const attIndex = attributes?.findIndex((f) => f.value.includes(value))
      const updatedAttributes = [...attributes]
      updatedAttributes[attIndex] = {
        traitType: trait,
        value: attributesArray.value.filter((f) => f !== value),
      }
      setAttributes(updatedAttributes.filter((f) => f.value.length > 0))
    } else {
      const prevValues =
        attributes?.find((f) => f?.traitType === trait)?.value ?? []

      if (attributes?.find((f) => f.traitType === trait)) {
        const attIndex = attributes?.findIndex((f) => f.traitType === trait)
        const updatedAttributes = [...attributes]
        updatedAttributes[attIndex] = {
          traitType: trait,
          value: [...prevValues, value],
        }
        setAttributes(updatedAttributes)
      } else {
        setAttributes([
          ...attributes,
          { traitType: trait, value: [...prevValues, value] },
        ])
      }
    }
    dispatch(setPage(1))
  }

  //#region Connecting Wallet If not Connected
  const [tryConnectWallet, setTryConnectWallet] = useState(false)
  const [trySwitchNetwork, setTrySwitchNetwork] = useState(false)

  const handleWalletConnect = (type: WALLET_TYPES) => {
    if (isTransactionInProgress) return
    dispatch(setTransactionStatus(TransactionEnum.STARTED))
    dispatch(setTransactionMessage('Connecting to wallet'))
    handleCloseConfirmMintModal()
    setIsTransactionInProgress(true)
    setTryConnectWallet(true)
    try {
      activate(getConnector(type))
    } catch (err) {
      console.log('error in wallet connect ', err)
    }
  }

  useEffect(() => {
    // Callback of handleWalletConnect
    if (tryConnectWallet) {
      if (account) {
        handleMintClick()
        setTryConnectWallet(false)
      }
      if (error && (error as any)?.code === 4902) {
        dispatch(setShowWrongNetworkModal(true))
        setTryConnectWallet(false)
        setIsTransactionInProgress(false)
      }
    }
  }, [tryConnectWallet, error, account])

  useEffect(() => {
    // Callback of SwitchNetwork
    // Continuing transaction after switching network
    if (trySwitchNetwork) {
      if (account && !error && active) {
        handleMintClick()
        setTryConnectWallet(false)
      }
    }
  }, [trySwitchNetwork, error, account])
  //#endregion

  //#region Mint Process
  const [isTransactionInProgress, setIsTransactionInProgress] = useState(false)

  const handleMintClick = async () => {
    const signer = provider?.getSigner()

    if (paymentMethod === 'crypto') {
      if (
        // Metamask not found
        user?.walletType === WalletTypeEnum.METAMASK &&
        (error as any) instanceof NoMetaMaskError
      ) {
        dispatch(
          showToast({
            message: 'Metamsk not found!',
            toastType: ToastType.success,
          })
        )
        return
      }

      // On wrong network
      if (user?.walletAddress && (error as any)?.code === 4902) {
        handleCloseConfirmMintModal()
        setTrySwitchNetwork(true)
        dispatch(setShowWrongNetworkModal(true))
        dispatch(setShowConnectModal(false))
        return
      }
      // Not Active
      if (!active) {
        handleWalletConnect(
          user?.walletType === WalletTypeEnum.METAMASK
            ? WALLET_TYPES.metamask
            : WALLET_TYPES.walletConnect
        )
        return
      }
      // Account different to profile account
      if (
        user?.walletAddress !== account &&
        user?.walletType === WalletTypeEnum.METAMASK
      ) {
        dispatch(
          showToast({
            message:
              "Connected metamask account and wallet address don't match. Please try again.",
            toastType: ToastType.error,
          })
        )
        return
      }

      // check for correct network

      if (chainId === getConfig().chainId) {
        handleCloseConfirmMintModal()
        setIsTransactionInProgress(true)
        dispatch(setTransactionStatus(TransactionEnum.STARTED))
        dispatch(setTransactionMessage('Waiting for Confirmation'))

        mintNFTS({
          account,
          crypto: cryptoMethod,
          dispatch,
          tokenIds: [
            ...selectedNFTs.map((item) => {
              return item.tokenId
            }),
          ],
          nftIds: [
            ...selectedNFTs.map((item) => {
              return item._id
            }),
          ],
          signer,
          provider,
        })
      } else {
        handleCloseConfirmMintModal()
        setTrySwitchNetwork(true)
        dispatch(setShowWrongNetworkModal(true))
        dispatch(setShowConnectModal(false))
      }
    } else {
      handleCloseConfirmMintModal()
      setIsTransactionInProgress(true)
      dispatch(setTransactionStatus(TransactionEnum.STARTED))
      dispatch(setTransactionMessage('Waiting for Payment Confirmation'))
      dispatch(
        mintNFTSFiat({
          nftIds: [
            ...selectedNFTs.map((item) => {
              return item._id
            }),
          ],
        })
      )
    }
  }
  //#endregion

  const handleClose = () => {
    if (
      transactionStatus === TransactionEnum.SUCCESS ||
      transactionStatus === TransactionEnum.ERROR
    ) {
      dispatch(setTransactionStatus(TransactionEnum.NOT_STARTED))
      dispatch(setTransactionHash(''))
      setIsTransactionInProgress(false)
    }
  }

  useEffect(() => {
    if (transactionStatus === TransactionEnum.SUCCESS) {
      handleClose()
      dispatch(setSuccessMessage('Your NFTs has successfully been minted.'))
      dispatch(setShowSuccessModal(true))
      setTryConnectWallet(false)
      setTrySwitchNetwork(false)
    }
  }, [transactionStatus])

  const observer = useRef<IntersectionObserver>()
  //write this code in your component
  const lastNftElementRef = useCallback(
    (node: any) => {
      if (observer.current) observer.current.disconnect()
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && nfts.length >= 12) {
          dispatch(setPage(page + 1))
        }
      })

      if (node) {
        observer.current.observe(node)
      }
    },
    [nfts.length, debouncedSearchTerm, nftStatus]
  )

  const handleNFTStatus = () => {
    //@ts-ignore
    dispatch(
      setNftStatus(
        showAvailableNfts ? NftStatusEnum.Available : NftStatusEnum.Sold
      )
    )
  }

  const handleCloseConfirmMintModal = () => {
    setShowConfirmMintModal(false)
    setIsTermsConditionsApproved(false)
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window?.scrollTo(0, 0)
    }
    handleNFTStatus()
  }, [showAvailableNfts])

  return (
    <SectionContainer className='gallery'>
      <Styles.StickyContainer>
        <Styles.HeadingCont isSticky={true}>
          <FlexRow justifyContent='space-between'>
            <h1>Gallery</h1>

            <Styles.MobileFilterWrap>
              <SplitButton
                width='48px'
                height='48px'
                className='icon'
                onClick={() => setShowMobileFilter(true)}
              >
                <Image src={SettingsSvg} alt='settings-icon' />
              </SplitButton>
            </Styles.MobileFilterWrap>
          </FlexRow>

          <Styles.TopButtonsCont>
            {attributes?.length ? (
              <SplitButton
                disabled={!attributes?.length}
                width={isMobile ? '122px' : '160px'}
                height='40px'
                padding='8px 16px'
                borderColor={colors.white}
                className='only-mobile'
                onClick={() => {
                  setAttributes([])
                }}
              >
                Clear Filters
              </SplitButton>
            ) : null}
            {selectedNFTs?.length ? (
              <SplitButton
                onClick={() => dispatch(setSelectedNFTs([]))}
                disabled={!selectedNFTs?.length}
                width={isMobile ? '141px' : '160px'}
                height='40px'
                padding='8px 16px'
                borderColor={colors.white}
              >
                Clear selection
              </SplitButton>
            ) : null}
            <SplitButton
              width={isMobile ? '81px' : '160px'}
              height='40px'
              padding='8px 16px'
              disabled={!selectedNFTs?.length}
              onClick={() => {
                if (!user?._id) {
                  dispatch(setShowRegisterModal(true))
                } else {
                  setShowConfirmMintModal(true)
                }
              }}
            >
              Mint{selectedNFTs?.length ? `(${selectedNFTs?.length})` : ''}
            </SplitButton>
          </Styles.TopButtonsCont>
        </Styles.HeadingCont>

        <Styles.NftContainer>
          {/* ============== Left container ============== */}

          <Styles.FilterWrapper isFilterApplied={!!attributes?.length}>
            <FlexCol gap='24px'>
              <InputWithIcon
                icon={SearchIcon}
                placeholder='Search NFTs'
                className='transparent'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              <InputContainer className='transparent'>
                <FlexRow justifyContent='space-between'>
                  <p>Available</p>

                  <Switch
                    checked={showAvailableNfts}
                    handleSwitch={setShowAvailableNfts}
                  />
                </FlexRow>
              </InputContainer>
            </FlexCol>

            <Styles.FilterCont>
              {filterOptionList?.map((item) => (
                <FilterDropdownV2
                  headerText={item.traitType}
                  key={item.traitType}
                >
                  <FlexCol gap='26px' margin='0 0 0 28px'>
                    {item?.value?.map((value) => (
                      <FlexRow key={item.traitType + value}>
                        <Styles.CheckBox
                          checked={attributes?.some((f) =>
                            f.value.includes(value)
                          )}
                          onClick={() =>
                            handleAttributesSelect(item.traitType, value)
                          }
                        >
                          {attributes?.some((f) => f.value.includes(value)) && (
                            <CheckIcon />
                          )}
                        </Styles.CheckBox>
                        <p>{value}</p>
                      </FlexRow>
                    ))}
                  </FlexCol>
                </FilterDropdownV2>
              ))}
            </Styles.FilterCont>
          </Styles.FilterWrapper>

          {/* ============== Right container ============== */}

          <FlexCol gap='24px'>
            {attributes?.length ? (
              <Styles.SelectedFiltersCont isSticky={true}>
                <SplitButton
                  width='120px'
                  height='40px'
                  padding='8px 16px'
                  onClick={() => {
                    setAttributes([])
                  }}
                >
                  Clear all
                </SplitButton>
              </Styles.SelectedFiltersCont>
            ) : null}

            {!!nfts?.length && (
              <Grid
                colGap='24px'
                rowGap='24px'
                gridItemMinWidth='243px'
                className='cardGrid'
              >
                {nfts?.map((item, index) => (
                  <Card
                    title={'VTR Connect'}
                    id={item?._id}
                    info={item?.info}
                    imageSrc={item?.image}
                    key={`${item?._id}-${index}`}
                    isSelected={selectedNFTs.find((f) => f._id === item._id)}
                    onNftClick={() => handleNftClick(item)}
                    tokenId={item?.tokenId}
                    ref={nfts.length === index + 1 ? lastNftElementRef : null}
                    isNftSold={item?.status === NftStatusEnum.Sold}
                  />
                ))}
              </Grid>
            )}

            <SkeletonTheme baseColor='#202020' highlightColor='#2393B8'>
              {loading && nfts?.length === 0 && (
                <Grid
                  colGap='24px'
                  rowGap='24px'
                  gridItemMinWidth='243px'
                  className='cardGrid'
                >
                  {Array(12)
                    .fill(0)
                    ?.map((_, index) => (
                      <LoadingCard key={'loading' + index} />
                    ))}
                </Grid>
              )}

              {loading && nfts?.length > 0 && (
                <Grid
                  colGap='24px'
                  rowGap='24px'
                  gridItemMinWidth='243px'
                  className='cardGrid'
                >
                  {Array(12)
                    .fill(0)
                    ?.map((_, index) => (
                      <LoadingCard key={'loading_more' + index} />
                    ))}
                </Grid>
              )}
            </SkeletonTheme>

            {!loading && nfts?.length === 0 && (
              <Styles.CardsWrapper>
                <h2>No NFTs found</h2>
              </Styles.CardsWrapper>
            )}
          </FlexCol>
        </Styles.NftContainer>
      </Styles.StickyContainer>

      {/* ============== Modals ============== */}

      <CustomModal
        show={!!selectedNFTs?.length && showConfirmMintModal}
        handleClose={() => handleCloseConfirmMintModal()}
        className='mint-modal'
      >
        <Modal
          onBtnClick={handleMintClick}
          heading='Mint Nft'
          isBtnDisabled={!isTermsConditionsApproved || !paymentMethod}
          headingContentGap='20px'
          isLoading={fiatLoading}
          fillOpacity='0.5'
          className='mint-modal'
          hasCloseBtn={isMobile ? true : false}
          onCloseClick={() => handleCloseConfirmMintModal()}
        >
          <SelectedNFTsCard
            setIsTermsConditionsApproved={setIsTermsConditionsApproved}
            isTermsConditionsApproved={isTermsConditionsApproved}
            showConfirmMintModal={showConfirmMintModal}
          />
        </Modal>
      </CustomModal>
      <CustomModal
        show={showMobileFilter}
        handleClose={() => setShowMobileFilter(false)}
      >
        <Styles.FilterWrapper className='mobile'>
          <FlexCol gap='24px'>
            <InputWithIcon
              icon={SearchIcon}
              placeholder='Search NFTs'
              className='transparent'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <InputContainer className='transparent'>
              <FlexRow justifyContent='space-between'>
                <p>Available</p>

                <Switch
                  checked={showAvailableNfts}
                  handleSwitch={setShowAvailableNfts}
                />
              </FlexRow>
            </InputContainer>
          </FlexCol>

          <Styles.FilterCont>
            {filterOptionList?.map((item) => (
              <FilterDropdownV2
                headerText={item.traitType}
                key={item.traitType}
              >
                <FlexCol gap='26px' margin='0 0 0 28px'>
                  {item?.value?.map((value) => (
                    <FlexRow key={item.traitType + value}>
                      <Styles.CheckBox
                        checked={attributes?.some((f) =>
                          f.value.includes(value)
                        )}
                        onClick={() =>
                          handleAttributesSelect(item.traitType, value)
                        }
                      >
                        {attributes?.some((f) => f.value.includes(value)) && (
                          <CheckIcon />
                        )}
                      </Styles.CheckBox>
                      <p>{value}</p>
                    </FlexRow>
                  ))}
                </FlexCol>
              </FilterDropdownV2>
            ))}
          </Styles.FilterCont>
        </Styles.FilterWrapper>
      </CustomModal>

      <StatusModal show={isTransactionInProgress} handleClose={handleClose} />
    </SectionContainer>
  )
}

export default Gallery
